import * as React from "react";
import {
  TableRow,
  TableCell,
  Box,
  Table,
  Checkbox,
  TableBody,
  TableHead,
} from "@mui/material";
import { useInjector } from "@bb-vendure/admin-ui/react";
import {
  NotificationService,
  ModalService,
  I18nService,
} from "@bb-vendure/admin-ui/core";
import { useFetchSpecByVarId } from "./graphql/Fetch-Spec-By-VarId";
import SpecRowInputForm from "./SpecRowInputForm";
import useDeleteSpec from "./graphql/Delete-Spec";
import { fontSize } from "@mui/system";

export type SpecForm = {
  id: string;
  name: string;
  value: string;
};

const SpecRow = (props: {
  id;
  name;
  defaultValues: {
    id: string;
    name: string;
    value: string;
  }[];
}) => {
  const notificationService = useInjector(NotificationService);
  const modalService = useInjector(ModalService);
  const translator = useInjector(I18nService);
  const { data, loading, error, refetch } = useFetchSpecByVarId(props.id);
  const [editMode, setEditMode] = React.useState<string[]>([]);
  const [deleteSpec] = useDeleteSpec();
  const [checkedItems, setCheckedItems] = React.useState<string[]>([]);
  const [newRows, setNewRows] = React.useState<
    { name: string; value: string }[]
  >([]);

  const addRow = () => {
    setNewRows((prevRows) => {
      //console.log(prevRows);
      if (prevRows.length === 0) {
        if (props.defaultValues.length <= 0) {
          return [...prevRows, { name: "", value: "" }];
        }
        return props.defaultValues.map((d) => {
          return {
            name: d.name,
            value: d.value,
          };
        });
      } else {
        notificationService.error("Please add the previous row first");
        return [...prevRows];
      }
    });
  };

  const handleCheckAll = (event) => {
    if (event.target.checked) {
      const newCheckedItems = (data as any).getSpecsByProductVariantId.map(
        (item) => item.id
      );
      setCheckedItems(newCheckedItems);
    } else {
      setCheckedItems([]);
    }
  };

  const onBulkDelete = () => {
    modalService
      .dialog({
        title: "Delete Spec",
        body: "Are you sure you want to delete these specs?",
        buttons: [
          { type: "secondary", label: "Cancel" },
          {
            type: "danger",
            label: "Delete",
            returnValue: true,
          },
        ],
      })
      .subscribe((res) => {
        if (res) {
          checkedItems.forEach((id) => {
            deleteSpec({ id });
          });
          refetch({ id: props.id });
          notificationService.success("Spec deleted successfully");
        }
      });
  };

  const onDelete = (id: string) => {
    modalService
      .dialog({
        title: "Delete Spec",
        body: "Are you sure you want to delete this spec?",
        buttons: [
          { type: "secondary", label: "Cancel" },
          {
            type: "danger",
            label: "Delete",
            returnValue: true,
          },
        ],
      })
      .subscribe((res) => {
        if (res) {
          deleteSpec({ id }).then(() => {
            refetch({ id: props.id });
            notificationService.success("Spec deleted successfully");
          });
        }
      });
  };

  React.useEffect(() => {
    if (data && (data as any).getSpecsByProductVariantId.length > 0) {
      const specsData = (data as any).getSpecsByProductVariantId as SpecForm[];

      const removeDuplicate = newRows.filter((r) =>
        specsData.find((s) => s.name !== r.name)
      );

      setNewRows(removeDuplicate);
    }
  }, [data]);

  return (
    <>
      <Box
        key={props.id}
        sx={{
          mb: 2,
          p: 2,
          mr: 5,
          borderRadius: 2,
          border: "1px dashed grey",
          width: "100%",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ marginTop: "0px", fontSize: 16 }}>{props.name}</div>
          {checkedItems && checkedItems.length > 0 && (
            <button className="button-ghost" onClick={onBulkDelete}>
              Delete {checkedItems.length} Spec
            </button>
          )}
          <button className="button-ghost" onClick={addRow}>
            {translator.translate('common.add-more')}
          </button>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pb: 0, pt: 0 }}>
                <Checkbox
                  size="small"
                  onChange={handleCheckAll}
                  checked={
                    checkedItems.length === 0
                      ? false
                      : checkedItems.length ===
                        (data as any).getSpecsByProductVariantId.length
                      ? true
                      : undefined
                  }
                  indeterminate={
                    checkedItems.length > 0 &&
                    checkedItems.length <
                      (data as any).getSpecsByProductVariantId.length
                  }
                />
              </TableCell>
              <TableCell sx={{ fontSize: "16px", pb: 0, pt: 0 }}>
                {translator.translate("catalog.specs-name")}
              </TableCell>
              <TableCell sx={{ fontSize: "16px", pb: 0, pt: 0 }}>
                {translator.translate('common.value')}
              </TableCell>
              <TableCell sx={{ fontSize: "16px", pb: 0, pt: 0 }}>
              {translator.translate('common.action')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data && (data as any).getSpecsByProductVariantId.length > 0 ? (
              [...(data as any).getSpecsByProductVariantId]
                .sort((a, b) => {
                  return (
                    new Date(a.createdAt).getTime() -
                    new Date(b.createdAt).getTime()
                  );
                })
                .map((item: SpecForm) => {
                  const isChecked = checkedItems.includes(item.id);

                  if (editMode.includes(item.id)) {
                    return (
                      <SpecRowInputForm
                        key={item.id}
                        id={props.id}
                        data={data}
                        cancel={() => {
                          setEditMode((prev) =>
                            prev.filter((id) => id !== item.id)
                          );
                          refetch({ id: props.id });
                        }}
                        editId={item.id}
                      />
                    );
                  }

                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          onChange={(checked) => {
                            if (checked.target.checked) {
                              setCheckedItems((prev) => prev.concat(item.id));
                            } else {
                              setCheckedItems((prev) =>
                                prev.filter((id) => id !== item.id)
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: "15px" }}>
                        {item.name}
                      </TableCell>
                      <TableCell style={{ fontSize: "15px" }}>
                        {item.value}
                      </TableCell>
                      <TableCell>
                        <button
                          className="button-ghost"
                          onClick={() => setEditMode(editMode.concat(item.id))}
                        >
                          Edit
                        </button>
                        <button
                          className="button-ghost"
                          onClick={() => onDelete(item.id)}
                        >
                          Delete
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <>
                {newRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      style={{ fontSize: "15px" }}
                      align="center"
                    >
                      {translator.translate('catalog.no-information')}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}

            {newRows.map((r, i) => {
              return (
                <SpecRowInputForm
                  key={i}
                  id={props.id}
                  data={data}
                  defaultInfor={{
                    name: r.name,
                    value: r.value,
                  }}
                  cancel={() => {
                    setNewRows((prevRows) =>
                      prevRows.filter((_, index) => index !== i)
                    );
                    refetch({ id: props.id });
                  }}
                />
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

export default SpecRow;
